/**
 * Admin Question Bank — view, edit, add and delete MCQ items.
 *
 * Concepts are listed grouped by domain (DS / CS / AI). Each concept card
 * is collapsible and shows every question with inline edit / delete
 * buttons. A "+ Add question" button appends a new MCQ to a concept.
 *
 * Notes:
 *  • DS-IN-01 questions are profile-rendered (per-country) and therefore
 *    shown read-only with a banner explaining why. Other 35 concepts are
 *    fully editable.
 *  • Default questions stay marked as "default" until edited; once edited
 *    they flip to "edited" so the audit trail is preserved server-side.
 */
import { useEffect, useState } from "react";
import { Plus, Save, Trash2, ChevronDown, ChevronRight, FileSpreadsheet, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  adminListQuestions,
  adminUpsertQuestion,
  adminDeleteQuestion,
  adminQuestionsXlsxUrl,
  adminToken,
} from "@/lib/api";
import DomainBadge from "@/components/DomainBadge";
import DownloadButton from "@/components/DownloadButton";
import { useT } from "@/lib/i18n";

export default function QuestionBank() {
  const { t } = useT();
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});  // cid → bool
  const [editing, setEditing] = useState(null);  // qid being edited
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminListQuestions();
      setConcepts(r.concepts || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (cid, q) => {
    setEditing(q.id);
    setDraft({
      id: q.id,
      concept_id: cid,
      q: q.q,
      options: [...q.options, "", "", "", ""].slice(0, 4),
      correct: q.correct,
    });
  };

  const startNew = (cid) => {
    setEditing(`new-${cid}`);
    setDraft({
      id: null,
      concept_id: cid,
      q: "",
      options: ["", "", "", ""],
      correct: 0,
    });
  };

  const cancelEdit = () => { setEditing(null); setDraft(null); };

  const save = async () => {
    if (!draft) return;
    if (!draft.q.trim()) { toast.error(t("bank.err_q")); return; }
    if (draft.options.some((o) => !o.trim())) {
      toast.error(t("bank.err_options")); return;
    }
    try {
      const payload = { ...draft };
      if (!payload.id) delete payload.id;
      await adminUpsertQuestion(payload);
      toast.success(t("bank.saved"));
      setEditing(null); setDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const remove = async (qid) => {
    if (!window.confirm(t("bank.confirm_delete"))) return;
    try {
      await adminDeleteQuestion(qid);
      toast.success(t("bank.deleted"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="question-bank" className="mt-8">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-[var(--iddl-muted)] flex-1">
          {t("bank.subtitle")}
        </p>
        <DownloadButton
          url={`${adminQuestionsXlsxUrl()}?admin_token=${encodeURIComponent(adminToken.get())}`}
          filename="IDDL_Question_Bank.xlsx"
          label={t("bank.download_xlsx")}
          icon={<FileSpreadsheet size={14} className="ml-2" />}
          data-testid="btn-bank-download-xlsx"
        />
        <Button variant="outline" className="rounded-none" onClick={load} data-testid="btn-bank-refresh">
          <RefreshCw size={14} className="mr-2" /> {t("admin.refresh")}
        </Button>
      </div>

      {err && <div className="mt-4 text-sm text-[var(--iddl-cs)]">{err}</div>}
      {loading && <div className="mt-6 label-mono">{t("admin.loading")}</div>}

      <div className="mt-6 grid gap-3" data-testid="bank-concepts">
        {concepts.map((c) => {
          const isOpen = !!open[c.concept_id];
          const isProfile = c.concept_id === "DS-IN-01";
          return (
            <div key={c.concept_id} className="paper" data-testid={`bank-concept-${c.concept_id}`}>
              <button
                type="button"
                onClick={() => setOpen({ ...open, [c.concept_id]: !isOpen })}
                className="w-full text-left p-4 flex items-center gap-3 flex-wrap hover:bg-[var(--iddl-surface)]"
                data-testid={`bank-concept-toggle-${c.concept_id}`}
              >
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <DomainBadge domain={c.domain} />
                <code className="label-mono">{c.concept_id}</code>
                <span className="font-display font-semibold">{c.concept_title}</span>
                <span className="label-mono text-[var(--iddl-muted)]">{c.subdomain}</span>
                <span className="ms-auto label-mono">{c.questions.length} {t("bank.questions")}</span>
              </button>

              {isOpen && (
                <div className="border-t border-[var(--iddl-rule)] p-4 grid gap-3">
                  {isProfile && (
                    <div className="text-xs p-3 surface border border-[var(--iddl-rule)]" data-testid="bank-profile-banner">
                      {t("bank.profile_locked")}
                    </div>
                  )}

                  {c.questions.map((q, qi) => (
                    <div key={q.id} className="border border-[var(--iddl-rule)] p-4" data-testid={`bank-q-${q.id}`}>
                      {editing === q.id ? (
                        <QuestionEditor
                          t={t}
                          draft={draft}
                          setDraft={setDraft}
                          onSave={save}
                          onCancel={cancelEdit}
                        />
                      ) : (
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="label-mono">Q{qi + 1}</span>
                            <span className={`label-mono px-2 py-0.5 border ${q.source === "default" ? "border-[var(--iddl-rule)]" : q.source === "edited" ? "border-amber-400 bg-amber-50" : "border-emerald-500 bg-emerald-50"}`}>
                              {q.source}
                            </span>
                            {!isProfile && (
                              <div className="ms-auto flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-none"
                                  onClick={() => startEdit(c.concept_id, q)}
                                  data-testid={`btn-edit-q-${q.id}`}
                                >
                                  {t("bank.edit")}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-none text-rose-600 border-rose-300"
                                  onClick={() => remove(q.id)}
                                  data-testid={`btn-delete-q-${q.id}`}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            )}
                          </div>
                          <p className="font-display font-semibold mt-2">{q.q}</p>
                          <ul className="mt-2 grid gap-1 text-sm">
                            {q.options.map((o, oi) => (
                              <li
                                key={oi}
                                className={`p-2 border ${oi === q.correct ? "border-emerald-500 bg-emerald-50" : "border-[var(--iddl-rule)]"}`}
                              >
                                {oi === q.correct && <span className="label-mono text-emerald-700 mr-2">✓ MODEL</span>}
                                {o}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}

                  {!isProfile && editing === `new-${c.concept_id}` && draft && (
                    <div className="border border-emerald-400 bg-emerald-50/30 p-4">
                      <QuestionEditor
                        t={t}
                        draft={draft}
                        setDraft={setDraft}
                        onSave={save}
                        onCancel={cancelEdit}
                      />
                    </div>
                  )}
                  {!isProfile && editing !== `new-${c.concept_id}` && (
                    <Button
                      variant="outline"
                      className="rounded-none border-dashed"
                      onClick={() => startNew(c.concept_id)}
                      data-testid={`btn-add-q-${c.concept_id}`}
                    >
                      <Plus size={14} className="mr-2" /> {t("bank.add_question")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuestionEditor({ t, draft, setDraft, onSave, onCancel }) {
  return (
    <div className="grid gap-3" data-testid="question-editor">
      <div>
        <Label className="label-mono">{t("bank.question_text")}</Label>
        <Textarea
          rows={2}
          className="mt-1 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
          value={draft.q}
          onChange={(e) => setDraft({ ...draft, q: e.target.value })}
          data-testid="editor-q"
        />
      </div>
      <div>
        <Label className="label-mono">{t("bank.options_label")}</Label>
        <div className="grid gap-2 mt-1">
          {draft.options.map((opt, oi) => (
            <div key={oi} className="flex items-center gap-2" data-testid={`editor-row-${oi}`}>
              <input
                type="radio"
                name="correct"
                checked={draft.correct === oi}
                onChange={() => setDraft({ ...draft, correct: oi })}
                data-testid={`editor-correct-${oi}`}
                title={t("bank.mark_correct")}
              />
              <span className="label-mono w-6">{String.fromCharCode(65 + oi)}.</span>
              <Input
                value={opt}
                onChange={(e) => {
                  const opts = [...draft.options];
                  opts[oi] = e.target.value;
                  setDraft({ ...draft, options: opts });
                }}
                className="h-9 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
                data-testid={`editor-opt-${oi}`}
                placeholder={`${t("bank.option")} ${String.fromCharCode(65 + oi)}`}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-[var(--iddl-muted)] mt-2">
          {t("bank.correct_hint")}
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button onClick={onSave} className="btn-ink rounded-none" data-testid="btn-editor-save">
          <Save size={14} className="mr-2" /> {t("bank.save")}
        </Button>
        <Button variant="outline" onClick={onCancel} className="rounded-none" data-testid="btn-editor-cancel">
          <X size={14} className="mr-2" /> {t("bank.cancel")}
        </Button>
      </div>
    </div>
  );
}
